import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from 'src/layout';
import Container from '@/components/ui/container';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/lib/axiosInstance';
import { toast } from 'react-toastify';
import {
    RiStoreLine,
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiSearchLine,
    RiRefreshLine,
    RiPercentLine,
    RiBuildingLine,
    RiShieldCheckLine
} from 'react-icons/ri';

const ListBrands = () => {
    const navigate = useNavigate();
    const [brandList, setBrandList] = useState([]);
    const [loadingAPI, setLoadingAPI] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [brandToDelete, setBrandToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchBrands = useCallback(async () => {
        try {
            setLoadingAPI(true);
            setError('');
            const { data } = await axiosInstance.get('/admin/brands');
            if (data.success) {
                setBrandList(data.data || []);
            } else {
                setError('Failed to fetch brands');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch brands');
        } finally {
            setLoadingAPI(false);
        }
    }, []);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const filteredBrands = useMemo(() => {
        if (!searchTerm) return brandList;
        const term = searchTerm.toLowerCase();
        return brandList.filter(brand =>
            brand.name?.toLowerCase().includes(term) ||
            brand.username?.toLowerCase().includes(term) ||
            brand.emailID?.toLowerCase().includes(term) ||
            brand.uid?.toString().includes(term)
        );
    }, [brandList, searchTerm]);

    const handleDeleteClick = (brand) => {
        setBrandToDelete(brand);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!brandToDelete) return;
        try {
            setDeleting(true);
            const { data } = await axiosInstance.delete(`/admin/brands/${brandToDelete.uid}`);
            if (data.success) {
                toast.success('Brand partner deleted successfully');
                setDeleteModalOpen(false);
                setBrandToDelete(null);
                fetchBrands();
            } else {
                toast.error(data.message || 'Failed to delete brand');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete brand');
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Layout active={'admin-brands-list'} title={'Brands Management'}>
            <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

                {/* 🚀 HEADER BANNER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <RiStoreLine className="text-indigo-600" /> Brands & Vendors Directory
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Manage registered brand partners, commission rates, GSTIN verification, and vendor access credentials.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchBrands}
                            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition"
                            title="Refresh Brands"
                        >
                            <RiRefreshLine className={loadingAPI ? 'animate-spin text-indigo-600' : ''} />
                        </button>

                        <button
                            onClick={() => navigate('/brands/add')}
                            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition"
                        >
                            <RiAddLine className="text-lg" /> Add New Brand
                        </button>
                    </div>
                </div>

                {/* 🔍 SEARCH & FILTERS BAR */}
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="relative flex-1 w-full max-w-md">
                        <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by brand name, email, or UID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-xs font-medium border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                        />
                    </div>

                    <div className="text-xs font-semibold text-gray-500">
                        Total Registered Brands: <span className="font-bold text-gray-900">{filteredBrands.length}</span>
                    </div>
                </div>

                {error && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-xs font-semibold">
                        {error}
                    </div>
                )}

                {/* 📋 TABLE CONTAINER */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {loadingAPI ? (
                        <TableSkeleton rows={5} />
                    ) : filteredBrands.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto mb-3">
                                <RiStoreLine className="text-3xl" />
                            </div>
                            <h3 className="text-base font-bold text-gray-900">No Brands Found</h3>
                            <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                                No vendor brand accounts match your search query.
                            </p>
                            <button
                                onClick={() => navigate('/brands/add')}
                                className="mt-4 inline-flex items-center gap-2 bg-indigo-600 text-white text-xs font-semibold px-4 py-2 rounded-xl"
                            >
                                <RiAddLine /> Register New Brand
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                        <th className="py-3.5 px-4">UID</th>
                                        <th className="py-3.5 px-4">Brand Information</th>
                                        <th className="py-3.5 px-4 text-center">GSTIN</th>
                                        <th className="py-3.5 px-4 text-center">Verification Status</th>
                                        <th className="py-3.5 px-4 text-center">Commission %</th>
                                        <th className="py-3.5 px-4 text-center">Joined Date</th>
                                        <th className="py-3.5 px-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
                                    {filteredBrands.map((brand) => (
                                        <tr key={brand.uid} className="hover:bg-gray-50/80 transition">
                                            {/* UID */}
                                            <td className="py-3.5 px-4 font-mono text-[11px] text-gray-400">
                                                {brand.uid}
                                            </td>

                                            {/* Brand Info */}
                                            <td className="py-3.5 px-4">
                                                <div className="flex gap-3 items-center">
                                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden shadow-sm shrink-0">
                                                        {brand.profilePhoto ? (
                                                            <img
                                                                src={brand.profilePhoto}
                                                                alt={brand.name || brand.username}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-white font-bold text-sm">
                                                                {brand.name?.charAt(0)?.toUpperCase() || brand.username?.charAt(0)?.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-gray-900 text-sm line-clamp-1">{brand.name || 'Unnamed Brand'}</div>
                                                        <div className="text-xs text-indigo-600 font-medium">{brand.emailID || 'No email'}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* GSTIN */}
                                            <td className="py-3.5 px-4 text-center font-mono text-[11px] text-gray-600">
                                                {brand.gstin || <span className="text-gray-300 italic">Not Provided</span>}
                                            </td>

                                            {/* Status */}
                                            <td className="py-3.5 px-4 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider ${
                                                    brand.verifiedEmail
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                                }`}>
                                                    <RiShieldCheckLine /> {brand.verifiedEmail ? 'Verified' : 'Pending'}
                                                </span>
                                            </td>

                                            {/* Commission */}
                                            <td className="py-3.5 px-4 text-center font-bold text-indigo-600">
                                                {brand.commissionPercentage !== null && brand.commissionPercentage !== undefined
                                                    ? `${brand.commissionPercentage}%`
                                                    : 'Standard'}
                                            </td>

                                            {/* Joined Date */}
                                            <td className="py-3.5 px-4 text-center text-gray-500">
                                                {formatDate(brand.createdOn || brand.joinedOn)}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3.5 px-4 text-center">
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <button
                                                        onClick={() => navigate(`/brands/edit/${brand.uid}`)}
                                                        className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition border border-indigo-100"
                                                        title="Edit Brand Settings"
                                                    >
                                                        <RiEditLine className="text-base" />
                                                    </button>

                                                    <button
                                                        onClick={() => handleDeleteClick(brand)}
                                                        className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition border border-rose-100"
                                                        title="Delete Brand Account"
                                                    >
                                                        <RiDeleteBinLine className="text-base" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* DELETE CONFIRMATION MODAL */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-5 border border-gray-100 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-gray-900">Delete Brand Account?</h3>
                        <p className="text-xs text-gray-500">
                            Are you sure you want to delete brand <strong className="text-gray-900">{brandToDelete?.name || brandToDelete?.username}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                onClick={() => setDeleteModalOpen(false)}
                                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl disabled:opacity-50"
                            >
                                {deleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ListBrands;
